import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api, getStrategyOpsCatalogDiagnostics, getStrategyOpsBacktestJob, listStrategyOpsBacktestReports, listStrategyOpsClientVisibleStrategies, runStrategyOpsBacktest } from '../lib_api';

const presets: Record<string, Partial<any>> = {
  'M5 baseline': { timeframe: 'M5', source: 'csv' },
  'H1/H4 EMA pullback validation': { timeframe: 'H1', source: 'csv' },
  'London only': { preset: 'london-only' },
  'New York only': { preset: 'new-york-only' },
  'London + New York strict': { preset: 'london-newyork-strict' },
  'Conservative risk': { preset: 'conservative-risk' },
  'Stress test with spread/slippage': { preset: 'stress-spread-slippage', spread: 22, slippage: 0.2 },
};

const isDebugEnabled = () => import.meta.env.DEV || localStorage.getItem('PICKAXE_ADMIN_BACKTEST_DEBUG') === '1';

export default function StrategyOps() {
  const [tab, setTab] = useState<'ops'|'lab'>('ops');
  const [selected, setSelected] = useState<string[]>([]);
  const [jobId, setJobId] = useState('');
  const [uiError, setUiError] = useState<any>(null);
  const [cfg, setCfg] = useState<any>({ symbol:'XAUUSD', timeframe:'M5', source:'csv', csvPath:'data/history/XAUUSD_M5_2025-03-03_0100_2026-02-26_2355.csv', allowPartial:true, maxSeconds:180 });
  const strategies = useQuery({ queryKey: ['admin', 'strategy-ops', 'strategies'], queryFn: listStrategyOpsClientVisibleStrategies });
  const runtime = useQuery({ queryKey: ['admin', 'strategy-ops', 'runtime-status'], queryFn: async () => (await api.get('/api/system/status')).data });
  const visibleQ = useQuery({ queryKey:['admin','strategy-ops','visible-client'], queryFn:listStrategyOpsClientVisibleStrategies });
  const reportsQ = useQuery({ queryKey:['admin','strategy-ops','reports'], queryFn:listStrategyOpsBacktestReports });
  const diagQ = useQuery({ queryKey:['admin','strategy-ops','diag'], queryFn:getStrategyOpsCatalogDiagnostics });
  const jobQ = useQuery({ queryKey:['admin','strategy-ops','job',jobId], queryFn:()=>getStrategyOpsBacktestJob(jobId), enabled:!!jobId, refetchInterval:2000 });
  const runM = useMutation({ mutationFn: runStrategyOpsBacktest, onSuccess:(d:any)=>setJobId(d.jobId) });
  const liveStatus = runM.isPending ? 'running' : (jobQ.data?.status || runM.data?.status || 'idle');
  const endpointResult = runM.data || null;
  const endpointError = (runM.error as any)?.response?.data?.message || (runM.error as any)?.response?.data?.error || (runM.error as any)?.message || null;

  const visible = visibleQ.data?.items || [];
  const visibleKeySet = useMemo(() => new Set(visible.map((s:any) => s.key)), [visible]);
  const selectableVisibleKeySet = useMemo(() => new Set(visible.filter((s:any) => s.adminBacktestEnabled && s.executableFound).map((s:any) => s.key)), [visible]);
  const selectedStrategyKeys = useMemo(() => selected.filter((k) => selectableVisibleKeySet.has(k)), [selected, selectableVisibleKeySet]);
  const failedVisible = visible.filter((s:any)=> reportsQ.data?.items?.some((r:any)=>(r.results||[]).some((x:any)=>x.strategyKey===s.key && x.status==='failed'))).map((s:any)=>s.key);
  const leaderboard = [...(jobQ.data?.results||[])].sort((a:any,b:any)=>(Number(b?.metrics?.profit_factor||0)-Number(a?.metrics?.profit_factor||0)));

  const runSelectedBacktest = async () => {
    const payload = { ...cfg, strategies: selectedStrategyKeys };
    if (selectedStrategyKeys.length === 0) {
      const err = {
        error: 'NO_BACKTEST_STRATEGIES_SELECTED',
        message: 'Select at least one client-visible strategy to test.',
        selectedKeys: selectedStrategyKeys,
      };
      setUiError(err);
      console.error('[ADMIN_BACKTEST_EMPTY_SELECTION_CLICK]', err);
      return;
    }

    if (isDebugEnabled()) {
      console.groupCollapsed('[ADMIN_BACKTEST_DEBUG] Test selected');
      console.log('[ADMIN_BACKTEST_UI_CLICK]', {
        selectedStrategyKeys,
        selectedCount: selectedStrategyKeys.length,
        visibleStrategyKeys: visible.map((s:any) => s.key),
        visibleCount: visible.length,
        requestPayload: payload,
      });
      console.log('[ADMIN_BACKTEST_DEBUG_ENDPOINT]', '/api/admin/strategy-ops/backtests/run');
      console.groupEnd();
    }

    setUiError(null);
    try {
      const rawResponse: any = await runM.mutateAsync(payload);
      if (isDebugEnabled()) {
        console.log('[ADMIN_BACKTEST_UI_RESPONSE]', {
          status: rawResponse?.status,
          jobId: rawResponse?.jobId,
          selectedStrategies: rawResponse?.selectedStrategies,
          totalStrategies: rawResponse?.totalStrategies,
          rawResponse,
        });
      }
      if (isDebugEnabled()) {
        console.log('[ADMIN_BACKTEST_RESOLUTION_DEBUG]', {
          selectedStrategyKeys,
          backendMessage: rawResponse?.message,
          allowedStrategies: rawResponse?.allowedStrategies,
          rejectedStrategies: rawResponse?.rejectedStrategies,
          failedStrategies: rawResponse?.failedStrategies,
          resolvedStrategies: rawResponse?.resolvedStrategies,
        });
      }
      if (!Array.isArray(rawResponse?.selectedStrategies) || rawResponse.selectedStrategies.length === 0) {
        console.error('[ADMIN_BACKTEST_EMPTY_SELECTION_RESPONSE]', {
          selectedStrategyKeys,
          requestPayload: payload,
          rawResponse,
          possibleCause: 'Frontend selected keys did not match backend client-visible allowlist or payload field name mismatch.',
        });
      }
      if (Number(rawResponse?.totalStrategies || 0) === 0) {
        console.error('[ADMIN_BACKTEST_ZERO_TOTAL_STRATEGIES]', { selectedStrategyKeys, requestPayload: payload, rawResponse });
      }
    } catch (error: any) {
      const rawResponse = error?.response?.data;
      const status = error?.response?.status;
      const errPayload = {
        error: rawResponse?.error || 'BACKTEST_REQUEST_FAILED',
        message: rawResponse?.message || error?.message || 'Backtest request failed.',
        selectedKeys: selectedStrategyKeys,
        rejectedKeys: rawResponse?.rejectedStrategies || [],
        failedStrategies: rawResponse?.failedStrategies || [],
        resolvedStrategies: rawResponse?.resolvedStrategies || [],
        allowedCount: Array.isArray(rawResponse?.allowedStrategies) ? rawResponse.allowedStrategies.length : undefined,
      };
      setUiError(errPayload);
      if (status === 400) {
        console.error('[ADMIN_BACKTEST_BACKEND_REJECTION]', {
          status,
          selectedStrategyKeys,
          requestPayload: payload,
          rawResponse,
          backendMessage: rawResponse?.message,
          allowedStrategies: rawResponse?.allowedStrategies,
          rejectedStrategies: rawResponse?.rejectedStrategies,
          failedStrategies: rawResponse?.failedStrategies,
          resolvedStrategies: rawResponse?.resolvedStrategies,
        });
        console.error('[ADMIN_BACKTEST_RESOLUTION_DEBUG]', {
          selectedStrategyKeys,
          backendMessage: rawResponse?.message,
          allowedStrategies: rawResponse?.allowedStrategies,
          rejectedStrategies: rawResponse?.rejectedStrategies,
          failedStrategies: rawResponse?.failedStrategies,
          resolvedStrategies: rawResponse?.resolvedStrategies,
        });
      } else {
        console.error('[ADMIN_BACKTEST_API_FAILURE]', { status, selectedStrategyKeys, requestPayload: payload, rawResponse, error });
      }
    }
  };

  return <div className='space-y-4'>
    <div className='flex gap-2'><button className='px-3 py-1 border rounded' onClick={()=>setTab('ops')}>Strategy Ops</button><button className='px-3 py-1 border rounded' onClick={()=>setTab('lab')}>Backtest Lab</button></div>
    {tab==='ops' && <section className='panel overflow-hidden'><div className='p-4 border-b'><h3 className='font-semibold'>Strategy Engine Visibility</h3></div><table className='min-w-full text-sm'><thead><tr><th>Strategy</th><th>Client Visible</th><th>Recommended</th><th>Mode</th></tr></thead><tbody>{(strategies.data?.items||[]).map((s:any)=><tr key={s.key}><td>{s.displayName}</td><td>{s.isClientVisible?'Yes':'No'}</td><td>{s.recommended?'Recommended':'-'}</td><td>{runtime.data?.decisionStatus?.runtimeSelectionMode||'-'}</td></tr>)}</tbody></table></section>}
    {tab==='lab' && <div className='space-y-3'>
      <div className='p-3 border rounded'><b>Catalog diagnostics</b>
        <div className='text-xs'>Client-visible strategies: {diagQ.data?.clientVisibleCount ?? '-'}</div>
        <div className='text-xs'>Admin-backtestable strategies: {diagQ.data?.adminBacktestableCount ?? '-'}</div>
        <div className='text-xs'>Hidden python-only strategies: {diagQ.data?.hiddenPythonOnlyCount ?? '-'}</div>
      </div>
      <div className='text-xs text-slate-600'>Admin backtesting is restricted to strategies visible to users in the web Strategy section.</div>
      <div className='p-3 border rounded'><div className='flex gap-2 flex-wrap'>{visible.map((s:any)=>{const enabled = s.adminBacktestEnabled && s.executableFound; const resolvedExecutionKey = s.resolvedExecutionKey || s.pythonStrategyKey; const copyCmd = `python -m backtesting.run --symbol XAUUSD --source csv --timeframe M5 --csv data/history/XAUUSD_M5_2025-03-03_0100_2026-02-26_2355.csv --strategy ${resolvedExecutionKey} --out reports/backtests/xauusd/admin/manual/${resolvedExecutionKey}`; return <label key={s.key} className='border rounded p-2'><input type='checkbox' disabled={!enabled} checked={selected.includes(s.key)} onChange={()=>setSelected(selected.includes(s.key)?selected.filter(x=>x!==s.key):[...selected,s.key])}/> {s.displayName} {!enabled ? <span className='ml-2 text-[11px] px-1 rounded bg-amber-100 text-amber-700'>Visible on web, no backtest implementation yet</span> : null}<span className='ml-2 text-[10px] text-slate-500'>{resolvedExecutionKey}</span><button type='button' className='ml-2 border px-1 rounded text-[11px] disabled:opacity-50' disabled={!enabled} onClick={()=>navigator.clipboard?.writeText(copyCmd)}>Copy command</button></label>;})}</div></div>
      <div className='p-3 border rounded space-y-2'>
        <input className='border p-1 w-full' value={cfg.csvPath} onChange={(e)=>setCfg({...cfg,csvPath:e.target.value})} />
        <div className='flex gap-2 flex-wrap'>
          {Object.entries(presets).map(([k,v])=><button key={k} className='border px-2 py-1 rounded' onClick={()=>setCfg({...cfg,...v})}>{k}</button>)}
        </div>
        <div className='flex gap-2 flex-wrap'>
          <button className='bg-indigo-600 text-white px-3 py-2 rounded disabled:opacity-60' disabled={runM.isPending} onClick={runSelectedBacktest}>{runM.isPending ? 'Testing…' : 'Test selected'}</button>
          <button className='border px-3 py-2 rounded' onClick={()=>runM.mutate({...cfg,strategies:'all-visible'})}>Test all visible client strategies</button>
          <button className='border px-3 py-2 rounded' onClick={()=>runM.mutate({...cfg,strategies:'remaining-visible'})}>Test remaining untested visible strategies</button>
          <button className='border px-3 py-2 rounded' onClick={()=>runM.mutate({...cfg,strategies:failedVisible.length?failedVisible:'failed-visible'})}>Retest failed only</button>
        </div>
        {uiError ? <div className='border border-red-300 bg-red-50 rounded p-2 text-xs text-red-700'><div><b>{uiError.error}</b></div><div>{uiError.message}</div><div>selected keys: {(uiError.selectedKeys || []).join(', ') || 'none'}</div><div>rejected keys: {(uiError.rejectedKeys || []).join(', ') || 'none'}</div><div>allowed keys count: {uiError.allowedCount ?? '-'}</div><div>failed strategies: {JSON.stringify(uiError.failedStrategies || [])}</div><div>resolved strategies: {(uiError.resolvedStrategies || []).join(', ') || 'none'}</div></div> : null}
        <div>Progress: {jobQ.data?.progress||0}% | current: {jobQ.data?.currentStrategy||'-'} | completed/failed/pending: {jobQ.data?.completed||0}/{jobQ.data?.failed||0}/{Math.max(0,(jobQ.data?.total||0)-((jobQ.data?.completed||0)+(jobQ.data?.failed||0)))}</div>
        <div className='text-sm'>
          Status: <b>{liveStatus === 'queued' ? 'queued' : liveStatus === 'running' ? 'running' : liveStatus === 'completed' ? 'completed' : runM.isError ? 'failed' : liveStatus}</b>
        </div>
        {endpointError ? <div className='text-sm text-red-600'>Backend error: {endpointError}</div> : null}
        {endpointResult ? <pre className='text-xs bg-slate-50 p-2 rounded overflow-auto'>{JSON.stringify(endpointResult, null, 2)}</pre> : null}
      </div>
    </div>}
  </div>;
}
